import {
  Component,
  OnDestroy,
  OnInit
} from '@angular/core';

import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { environment } from '../../../environments/environment';

import {
  Subject,
  debounceTime,
  distinctUntilChanged,
  takeUntil
} from 'rxjs';

import { ApiService } from '../../core/api.service';


type SettingsSection =
  | 'restaurant'
  | 'address'
  | 'appearance'
  | 'hours'
  | 'team'
  | 'security';


interface RestaurantListItem {
  id: number;
  name: string;
}


type TeamRole =
  | 'ADMIN'
  | 'ATTENDANT'
  | 'CASHIER';


interface TeamMember {
  id: number;
  userId?: number;
  name: string;
  email: string;
  role: TeamRole | 'OWNER';
  status?: string;
}


interface TeamInvitation {
  id: number;
  email: string;
  role: TeamRole;

  status:
    | 'PENDING'
    | 'ACCEPTED'
    | 'EXPIRED'
    | 'CANCELLED';

  createdAt?: string;
  expiresAt?: string;
}


interface TeamResponse {
  members?: TeamMember[];
  invitations?: TeamInvitation[];
}


/*
 * ==============================
 * ENTREGA
 * ==============================
 */

type DeliveryFeeType =
  | 'FIXED'
  | 'ZIP_CODE';


interface DeliveryFeeRule {
  id?: number;

  name: string;

  zipCodeStart: string;

  zipCodeEnd: string;

  fee: number;

  isActive: boolean;
}


type WeekDay =
  | 'SUNDAY'
  | 'MONDAY'
  | 'TUESDAY'
  | 'WEDNESDAY'
  | 'THURSDAY'
  | 'FRIDAY'
  | 'SATURDAY';


interface OpeningPeriod {
  id?: number;

  openTime: string;

  closeTime: string;
}


interface OpeningDay {
  id?: number;

  dayOfWeek: WeekDay;

  isOpen: boolean;

  periods: OpeningPeriod[];
}


interface OpeningStatus {
  isOpen: boolean;

  manualClosed?: boolean;

  reason?: string;

  message?: string;

  closesAt?: string;

  currentTime?: string;

  timezone?: string;
}

interface RestaurantSettings {
  id: number | null;

  name: string;

  description: string;

  phone: string;

  whatsapp: string;

  email: string;

  slug: string;

  logoUrl: string;

  coverUrl: string;

  primaryColor: string;

  secondaryColor: string;

  deliveryFeeType:
    DeliveryFeeType;

  deliveryFee:
    number;
}


@Component({
  selector: 'app-settings',

  standalone: true,

  imports: [
    CommonModule,
    FormsModule
  ],

  templateUrl:
    './settings.component.html',

  styleUrl:
    './settings.component.css'
})
export class SettingsComponent
  implements OnInit, OnDestroy {

  activeSection:
    SettingsSection =
    'restaurant';


  /*
   * ==============================
   * LOADINGS
   * ==============================
   */

  loadingRestaurants =
    false;

  loading =
    false;


/*
 * ==============================
 * FUNCIONAMENTO
 * ==============================
 */

loadingOpeningHours =
  false;

savingOpeningHours =
  false;

savingOpenStatus =
  false;


restaurantOpen =
  true;


restaurantTimezone =
  'America/Recife';


openingStatus:
  OpeningStatus | null =
  null;


openingHours:
  OpeningDay[] =
  [];


readonly weekDays:
  Array<{
    value: WeekDay;
    label: string;
  }> = [

    {
      value: 'MONDAY',
      label: 'Segunda-feira'
    },

    {
      value: 'TUESDAY',
      label: 'Terça-feira'
    },

    {
      value: 'WEDNESDAY',
      label: 'Quarta-feira'
    },

    {
      value: 'THURSDAY',
      label: 'Quinta-feira'
    },

    {
      value: 'FRIDAY',
      label: 'Sexta-feira'
    },

    {
      value: 'SATURDAY',
      label: 'Sábado'
    },

    {
      value: 'SUNDAY',
      label: 'Domingo'
    }

  ];



  savingRestaurant =
    false;

  savingAddress =
    false;

  savingAppearance =
    false;

  changingPassword =
    false;

  loadingTeam =
    false;

  invitingMember =
    false;


  updatingMemberId:
    number | null =
    null;


  removingMemberId:
    number | null =
    null;


  cancellingInvitationId:
    number | null =
    null;


  /*
   * ENTREGA
   */

  savingDeliverySettings =
    false;

  loadingDeliveryRules =
    false;

  savingDeliveryRule =
    false;


  /*
   * ==============================
   * MENSAGENS
   * ==============================
   */

  successMessage =
    '';

  errorMessage =
    '';


  /*
   * ==============================
   * RESTAURANTES
   * ==============================
   */

  restaurants:
    RestaurantListItem[] =
    [];


  restaurantId:
    number | null =
    null;


  /*
   * ==============================
   * ENTREGA
   * ==============================
   */

  deliveryRules:
    DeliveryFeeRule[] =
    [];


  /*
   * ==============================
   * EQUIPE
   * ==============================
   */

  teamMembers:
    TeamMember[] =
    [];


  teamInvitations:
    TeamInvitation[] =
    [];


  invitationEmail =
    '';


  invitationRole:
    TeamRole =
    'ATTENDANT';


  readonly teamRoleOptions:
    Array<{
      value: TeamRole;
      label: string;
      description: string;
    }> = [

      {
        value:
          'ADMIN',

        label:
          'Administrador',

        description:
          'Pode gerenciar configurações e a operação.'
      },

      {
        value:
          'ATTENDANT',

        label:
          'Atendente',

        description:
          'Pode acompanhar e atualizar pedidos.'
      },

      {
        value:
          'CASHIER',

        label:
          'Caixa',

        description:
          'Pode acessar pedidos e o fluxo financeiro.'
      }

    ];


  /*
   * ==============================
   * RESTAURANTE
   * ==============================
   */

  restaurant:
    RestaurantSettings = {

      id:
        null,

      name:
        '',

      description:
        '',

      phone:
        '',

      whatsapp:
        '',

      email:
        '',

      slug:
        '',

      logoUrl:
        '',

      coverUrl:
        '',

      primaryColor:
        '#ff5a1f',

      secondaryColor:
        '#111827',

      deliveryFeeType:
        'FIXED',

      deliveryFee:
        0
    };


  readonly platformDomain =
    environment.platformDomain;


  /*
   * ==============================
   * SLUG
   * ==============================
   */

  checkingSlug =
    false;


  slugAvailable:
    boolean | null =
    null;


  slugError =
    '';


  slugSuggestions:
    string[] =
    [];


  /*
   * ==============================
   * SENHA
   * ==============================
   */

  currentPassword =
    '';


  newPassword =
    '';


  confirmPassword =
    '';


  showCurrentPassword =
    false;


  showNewPassword =
    false;


  showConfirmPassword =
    false;


  /*
   * ==============================
   * RXJS
   * ==============================
   */

  private slugSubject =
    new Subject<string>();


  private destroy$ =
    new Subject<void>();


  constructor(
    private api:
      ApiService
  ) {}


  /*
   * ==============================
   * INIT
   * ==============================
   */

  ngOnInit(): void {

    this.configureSlugValidation();

    this.loadRestaurants();
  }


  ngOnDestroy(): void {

    this.destroy$
      .next();

    this.destroy$
      .complete();
  }


  /*
   * ==============================
   * SEÇÕES
   * ==============================
   */

  setSection(
    section:
      SettingsSection
  ): void {

    this.activeSection =
      section;

    this.clearMessages();


    if (
  section ===
    'hours' &&
  this.restaurantId
) {
  this.loadOpeningHours();
}


    if (
      section ===
        'team' &&
      this.restaurantId
    ) {

      this.loadTeam();
    }
  }



  /*
 * =================================================
 * FUNCIONAMENTO
 * =================================================
 */


/*
 * Cria a estrutura padrão da semana.
 */
private createDefaultOpeningHours():
  OpeningDay[] {

  return this.weekDays.map(
    day => ({

      dayOfWeek:
        day.value,

      isOpen:
        false,

      periods:
        []
    })
  );
}


/*
 * =====================================
 * CARREGA HORÁRIOS
 * =====================================
 */

loadOpeningHours(): void {

  if (
    !this.restaurantId
  ) {
    return;
  }


  this.loadingOpeningHours =
    true;

  this.clearMessages();


  this.api
    .get<any>(
      `/restaurants/${this.restaurantId}/opening-hours`
    )
    .subscribe({

      next:
        response => {

          this.restaurantOpen =
            response?.isOpen !==
            false;


          this.restaurantTimezone =
            response?.timezone ||
            'America/Recife';


          this.openingStatus =
            response?.status ??
            null;


          const savedHours:
            OpeningDay[] =
            response?.openingHours ??
            [];


          /*
           * Sempre mostramos os
           * sete dias da semana,
           * mesmo quando ainda não
           * existem no banco.
           */
          this.openingHours =
            this.weekDays.map(
              day => {

                const saved =
                  savedHours.find(
                    item =>
                      item.dayOfWeek ===
                      day.value
                  );


                if (!saved) {

                  return {
                    dayOfWeek:
                      day.value,

                    isOpen:
                      false,

                    periods:
                      []
                  };
                }


                return {

                  id:
                    saved.id,

                  dayOfWeek:
                    day.value,

                  isOpen:
                    saved.isOpen !==
                    false,

                  periods:
                    (
                      saved.periods ??
                      []
                    ).map(
                      period => ({

                        id:
                          period.id,

                        openTime:
                          period.openTime ??
                          '',

                        closeTime:
                          period.closeTime ??
                          ''
                      })
                    )
                };
              }
            );


          this.loadingOpeningHours =
            false;
        },


      error:
        error => {

          this.loadingOpeningHours =
            false;


          this.openingHours =
            this.createDefaultOpeningHours();


          this.errorMessage =

            error
              ?.error
              ?.message ??

            'Não foi possível carregar os horários de funcionamento.';
        }

    });
}


/*
 * =====================================
 * ABRIR / FECHAR DIA
 * =====================================
 */

toggleOpeningDay(
  day:
    OpeningDay
): void {

  day.isOpen =
    !day.isOpen;


  /*
   * Ao abrir um dia sem
   * horários, criamos um
   * horário inicial.
   */
  if (
    day.isOpen &&
    !day.periods.length
  ) {

    day.periods.push({
      openTime:
        '18:00',

      closeTime:
        '23:00'
    });
  }


  /*
   * Se fechar o dia,
   * removemos os períodos.
   */
  if (
    !day.isOpen
  ) {

    day.periods =
      [];
  }
}


/*
 * =====================================
 * ADICIONA PERÍODO
 * =====================================
 */

addOpeningPeriod(
  day:
    OpeningDay
): void {

  day.isOpen =
    true;


  day.periods.push({
    openTime:
      '18:00',

    closeTime:
      '23:00'
  });
}


/*
 * =====================================
 * REMOVE PERÍODO
 * =====================================
 */

removeOpeningPeriod(
  day:
    OpeningDay,

  index:
    number
): void {

  day.periods.splice(
    index,
    1
  );


  if (
    !day.periods.length
  ) {

    day.isOpen =
      false;
  }
}


/*
 * =====================================
 * SALVA HORÁRIOS
 * =====================================
 */

saveOpeningHours(): void {

  if (
    !this.restaurantId
  ) {
    return;
  }


  /*
   * Validação simples antes
   * de chamar o backend.
   */
  for (
    const day of
    this.openingHours
  ) {

    if (
      day.isOpen &&
      !day.periods.length
    ) {

      this.errorMessage =
        'Todo dia aberto precisa possuir pelo menos um horário.';

      return;
    }


    for (
      const period of
      day.periods
    ) {

      if (
        !period.openTime ||
        !period.closeTime
      ) {

        this.errorMessage =
          'Preencha todos os horários de abertura e fechamento.';

        return;
      }


      if (
        period.openTime ===
        period.closeTime
      ) {

        this.errorMessage =
          'O horário de abertura não pode ser igual ao horário de fechamento.';

        return;
      }
    }
  }


  this.savingOpeningHours =
    true;

  this.clearMessages();


  const payload = {

    timezone:
      this.restaurantTimezone,

    openingHours:
      this.openingHours.map(
        day => ({

          dayOfWeek:
            day.dayOfWeek,

          isOpen:
            day.isOpen,

          periods:
            day.isOpen
              ? day.periods.map(
                  period => ({

                    openTime:
                      period.openTime,

                    closeTime:
                      period.closeTime
                  })
                )
              : []
        })
      )
  };


  this.api
    .put<any>(
      `/restaurants/${this.restaurantId}/opening-hours`,
      payload
    )
    .subscribe({

      next:
        response => {

          this.savingOpeningHours =
            false;


          this.openingStatus =
            response?.status ??
            null;


          this.successMessage =
            'Horários de funcionamento atualizados com sucesso.';


          /*
           * Recarrega para utilizar
           * exatamente o que ficou
           * salvo no backend.
           */
          this.loadOpeningHours();
        },


      error:
        error => {

          this.savingOpeningHours =
            false;


          this.errorMessage =

            error
              ?.error
              ?.message ??

            'Não foi possível salvar os horários de funcionamento.';
        }

    });
}


/*
 * =====================================
 * ABRIR / FECHAR MANUALMENTE
 * =====================================
 */

toggleRestaurantOpenStatus(): void {

  if (
    !this.restaurantId ||
    this.savingOpenStatus
  ) {
    return;
  }


  const newStatus =
    !this.restaurantOpen;


  this.savingOpenStatus =
    true;

  this.clearMessages();


  this.api
    .patch<any>(
      `/restaurants/${this.restaurantId}/open-status`,
      {
        isOpen:
          newStatus
      }
    )
    .subscribe({

      next:
        response => {

          this.savingOpenStatus =
            false;


          this.restaurantOpen =
            response?.isOpen !==
            false;


          this.openingStatus =
            response?.status ??
            null;


          this.successMessage =
            this.restaurantOpen
              ? 'Restaurante liberado para funcionar.'
              : 'Restaurante fechado manualmente.';
        },


      error:
        error => {

          this.savingOpenStatus =
            false;


          this.errorMessage =

            error
              ?.error
              ?.message ??

            'Não foi possível alterar o funcionamento do restaurante.';
        }

    });
}


  /*
   * ==============================
   * LISTA RESTAURANTES
   * ==============================
   */

  loadRestaurants(): void {

    this.loadingRestaurants =
      true;

    this.errorMessage =
      '';


    this.api
      .get<RestaurantListItem[]>(
        '/restaurants'
      )
      .subscribe({

        next:
          restaurants => {

            this.restaurants =
              restaurants ??
              [];


            this.loadingRestaurants =
              false;


            if (
              !this.restaurants
                .length
            ) {

              this.restaurantId =
                null;


              this.errorMessage =
                'Nenhum restaurante foi encontrado para este usuário.';


              return;
            }


            const currentRestaurantExists =

              this.restaurantId !==
                null &&

              this.restaurants
                .some(
                  restaurant =>
                    restaurant.id ===
                    this.restaurantId
                );


            if (
              !currentRestaurantExists
            ) {

              this.restaurantId =
                this.restaurants[0]
                  .id;
            }


            this.loadSettings();
          },


        error:
          error => {

            this.loadingRestaurants =
              false;


            this.restaurants =
              [];


            this.restaurantId =
              null;


            this.errorMessage =

              error
                ?.error
                ?.message ??

              'Não foi possível carregar os restaurantes.';
          }

      });
  }


  /*
   * ==============================
   * TROCA RESTAURANTE
   * ==============================
   */

  onRestaurantChange(): void {

    this.slugAvailable =
      null;

    this.slugError =
      '';

    this.slugSuggestions =
      [];

    this.teamMembers =
      [];

    this.teamInvitations =
      [];

    this.deliveryRules =
      [];

      this.openingHours =
  [];

this.openingStatus =
  null;


    this.clearMessages();


    this.loadSettings();


    if (
      this.activeSection ===
      'team'
    ) {

      this.loadTeam();
    }



if (
  this.activeSection ===
  'hours'
) {
  this.loadOpeningHours();
}




  }




  
  /*
   * ==============================
   * CARREGA CONFIGURAÇÕES
   * ==============================
   */

  loadSettings(): void {

    if (
      !this.restaurantId
    ) {

      return;
    }


    this.loading =
      true;


    this.clearMessages();


    this.api
      .get<any>(
        `/restaurants/${this.restaurantId}`
      )
      .subscribe({

        next:
          response => {

            const data =
              response?.restaurant ??
              response;


            this.restaurant = {

              id:
                data?.id ??
                this.restaurantId,

              name:
                data?.name ??
                '',

              description:
                data?.description ??
                '',

              phone:
                data?.phone ??
                '',

              whatsapp:
                data?.whatsapp ??
                '',

              email:
                data?.email ??
                '',

              slug:
                data?.slug ??
                '',

              logoUrl:
                data?.logoUrl ??
                '',

              coverUrl:
                data?.coverUrl ??
                '',

              primaryColor:
                data
                  ?.primaryColor ??
                '#ff5a1f',

              secondaryColor:
                data
                  ?.secondaryColor ??
                '#111827',

              deliveryFeeType:
                data
                  ?.deliveryFeeType ===
                'ZIP_CODE'
                  ? 'ZIP_CODE'
                  : 'FIXED',

              deliveryFee:
                Number(
                  data
                    ?.deliveryFee ??
                  0
                )
            };


            this.slugAvailable =
              Boolean(
                this.restaurant
                  .slug
              );


            this.slugError =
              '';


            this.slugSuggestions =
              [];


            this.loading =
              false;


            /*
             * Carrega regiões somente
             * quando o restaurante usa
             * taxa por CEP.
             */
            if (
              this.restaurant
                .deliveryFeeType ===
              'ZIP_CODE'
            ) {

              this.loadDeliveryRules();

            } else {

              this.deliveryRules =
                [];
            }
          },


        error:
          error => {

            this.loading =
              false;


            this.errorMessage =

              error
                ?.error
                ?.message ??

              'Não foi possível carregar as configurações.';
          }

      });
  }


  /*
   * ==============================
   * SALVA RESTAURANTE
   * ==============================
   */

  saveRestaurant(): void {

    if (
      !this.restaurantId
    ) {

      return;
    }


    const name =
      this.restaurant
        .name
        .trim();


    const email =
      this.restaurant
        .email
        .trim();


    const whatsapp =
      this.normalizePhone(
        this.restaurant
          .whatsapp
      );


    if (!name) {

      this.errorMessage =
        'Informe o nome do restaurante.';

      return;
    }


    if (
      email &&
      !this.isValidEmail(
        email
      )
    ) {

      this.errorMessage =
        'Informe um e-mail válido.';

      return;
    }


    this.savingRestaurant =
      true;


    this.clearMessages();


    const payload = {

      name,

      description:
        this.restaurant
          .description
          .trim(),

      phone:
        this.normalizePhone(
          this.restaurant
            .phone
        ),

      whatsapp,

      email:
        email
          .toLowerCase()
    };


    this.api
      .put<any>(
        `/restaurants/${this.restaurantId}`,
        payload
      )
      .subscribe({

        next:
          response => {

            const updated =
              response?.restaurant ??
              response;


            this.restaurant = {
              ...this.restaurant,
              ...updated
            };


            this.savingRestaurant =
              false;


            this.successMessage =
              'Dados do restaurante atualizados com sucesso.';
          },


        error:
          error => {

            this.savingRestaurant =
              false;


            this.errorMessage =

              error
                ?.error
                ?.message ??

              'Não foi possível salvar os dados.';
          }

      });
  }


  /*
   * =================================================
   * ENTREGA
   * =================================================
   */


  /*
   * Alterou entre FIXED e ZIP_CODE.
   */
  onDeliveryFeeTypeChange(): void {

    this.clearMessages();


    if (
      this.restaurant
        .deliveryFeeType ===
      'ZIP_CODE'
    ) {

      this.loadDeliveryRules();

      return;
    }


    this.deliveryRules =
      [];
  }


  /*
   * ==============================
   * SALVA TIPO DE FRETE
   * ==============================
   */

  saveDeliverySettings(): void {

    if (
      !this.restaurantId
    ) {

      return;
    }


    const deliveryFee =
      Number(
        this.restaurant
          .deliveryFee ??
        0
      );


    if (
      Number.isNaN(
        deliveryFee
      ) ||

      deliveryFee < 0
    ) {

      this.errorMessage =
        'Informe uma taxa de entrega válida.';

      return;
    }


    this.savingDeliverySettings =
      true;


    this.clearMessages();


    /*
     * Mesmo no modo ZIP_CODE
     * preservamos a taxa fixa
     * cadastrada.
     *
     * Ela simplesmente não será
     * usada enquanto ZIP_CODE
     * estiver ativo.
     */
    const payload = {

      deliveryFeeType:
        this.restaurant
          .deliveryFeeType,

      deliveryFee
    };


    this.api
      .patch<any>(

        `/restaurants/${this.restaurantId}/delivery-settings`,

        payload
      )
      .subscribe({

        next:
          response => {

            const updated =
              response?.restaurant ??
              response;


            if (
              updated
            ) {

              this.restaurant = {

                ...this.restaurant,

                ...updated,

                deliveryFeeType:

                  updated
                    ?.deliveryFeeType ===
                  'ZIP_CODE'

                    ? 'ZIP_CODE'

                    : 'FIXED',

                deliveryFee:
                  Number(

                    updated
                      ?.deliveryFee ??

                    this.restaurant
                      .deliveryFee ??

                    0
                  )
              };
            }


            this.savingDeliverySettings =
              false;


            this.successMessage =
              'Configuração de entrega atualizada com sucesso.';


            if (
              this.restaurant
                .deliveryFeeType ===
              'ZIP_CODE'
            ) {

              this.loadDeliveryRules();

            } else {

              this.deliveryRules =
                [];
            }
          },


        error:
          error => {

            this.savingDeliverySettings =
              false;


            this.errorMessage =

              error
                ?.error
                ?.message ??

              'Não foi possível salvar a configuração de entrega.';
          }

      });
  }


  /*
   * ==============================
   * LISTA REGIÕES
   * ==============================
   */

  loadDeliveryRules(): void {

    if (
      !this.restaurantId
    ) {

      return;
    }


    this.loadingDeliveryRules =
      true;


    this.api
      .get<any[]>(

        `/restaurants/${this.restaurantId}/delivery-fee-rules`

      )
      .subscribe({

        next:
          response => {

            this.deliveryRules =

              (
                response ??
                []
              )
                .map(
                  rule => ({

                    id:
                      rule?.id,

                    name:
                      rule?.name ??
                      '',

                    zipCodeStart:
                      this
                        .formatStoredZipCode(
                          rule
                            ?.zipCodeStart
                        ),

                    zipCodeEnd:
                      this
                        .formatStoredZipCode(
                          rule
                            ?.zipCodeEnd
                        ),

                    fee:
                      Number(
                        rule?.fee ??
                        0
                      ),

                    isActive:
                      rule
                        ?.isActive !==
                      false
                  })
                );


            this.loadingDeliveryRules =
              false;
          },


        error:
          error => {

            this.loadingDeliveryRules =
              false;


            this.deliveryRules =
              [];


            this.errorMessage =

              error
                ?.error
                ?.message ??

              'Não foi possível carregar as regiões de entrega.';
          }

      });
  }


  /*
   * ==============================
   * ADICIONA NOVA REGIÃO
   * ==============================
   */

  addDeliveryRule(): void {

    this.deliveryRules
      .push({

        name:
          '',

        zipCodeStart:
          '',

        zipCodeEnd:
          '',

        fee:
          0,

        isActive:
          true
      });
  }


  /*
   * ==============================
   * SALVA REGIÃO
   * ==============================
   */

  saveDeliveryRule(
    rule:
      DeliveryFeeRule
  ): void {

    if (
      !this.restaurantId
    ) {

      return;
    }


    const name =
      String(
        rule.name ??
        ''
      )
        .trim();


    const zipCodeStart =
      this.normalizeZipCode(
        rule.zipCodeStart
      );


    const zipCodeEnd =
      this.normalizeZipCode(
        rule.zipCodeEnd
      );


    const fee =
      Number(
        rule.fee
      );


    if (!name) {

      this.errorMessage =
        'Informe o nome da região.';

      return;
    }


    if (
      zipCodeStart.length !==
        8 ||

      zipCodeEnd.length !==
        8
    ) {

      this.errorMessage =
        'Informe CEP inicial e final válidos.';

      return;
    }


    if (
      Number(
        zipCodeStart
      ) >

      Number(
        zipCodeEnd
      )
    ) {

      this.errorMessage =
        'O CEP inicial não pode ser maior que o CEP final.';

      return;
    }


    if (
      Number.isNaN(
        fee
      ) ||

      fee < 0
    ) {

      this.errorMessage =
        'Informe uma taxa válida.';

      return;
    }


    this.savingDeliveryRule =
      true;


    this.clearMessages();


    const payload = {

      name,

      zipCodeStart,

      zipCodeEnd,

      fee,

      isActive:
        rule.isActive
    };


    /*
     * Se possui ID:
     * atualiza.
     *
     * Se não possui:
     * cria.
     */
    const request$ =

      rule.id

        ? this.api
            .put<any>(

              `/restaurants/${this.restaurantId}/delivery-fee-rules/${rule.id}`,

              payload
            )

        : this.api
            .post<any>(

              `/restaurants/${this.restaurantId}/delivery-fee-rules`,

              payload
            );


    request$
      .subscribe({

        next:
          savedRule => {

            this.savingDeliveryRule =
              false;


            rule.id =
              savedRule?.id ??
              rule.id;


            rule.name =
              savedRule?.name ??
              name;


            rule.zipCodeStart =
              this
                .formatStoredZipCode(

                  savedRule
                    ?.zipCodeStart ??

                  zipCodeStart
                );


            rule.zipCodeEnd =
              this
                .formatStoredZipCode(

                  savedRule
                    ?.zipCodeEnd ??

                  zipCodeEnd
                );


            rule.fee =
              Number(

                savedRule
                  ?.fee ??

                fee
              );


            rule.isActive =
              savedRule
                ?.isActive !==
              false;


            this.successMessage =
              'Região de entrega salva com sucesso.';
          },


        error:
          error => {

            this.savingDeliveryRule =
              false;


            this.errorMessage =

              error
                ?.error
                ?.message ??

              'Não foi possível salvar a região de entrega.';
          }

      });
  }


  /*
   * ==============================
   * REMOVE REGIÃO
   * ==============================
   */

  removeDeliveryRule(
    rule:
      DeliveryFeeRule,

    index:
      number
  ): void {

    /*
     * Ainda não foi salva.
     */
    if (
      !rule.id
    ) {

      this.deliveryRules
        .splice(
          index,
          1
        );


      return;
    }


    if (
      !this.restaurantId
    ) {

      return;
    }


    const confirmed =
      window.confirm(

        `Excluir a região "${rule.name}"?`

      );


    if (
      !confirmed
    ) {

      return;
    }


    this.clearMessages();


    this.api
      .delete<void>(

        `/restaurants/${this.restaurantId}/delivery-fee-rules/${rule.id}`

      )
      .subscribe({

        next:
          () => {

            this.deliveryRules =

              this.deliveryRules
                .filter(
                  item =>
                    item.id !==
                    rule.id
                );


            this.successMessage =
              'Região de entrega excluída.';
          },


        error:
          error => {

            this.errorMessage =

              error
                ?.error
                ?.message ??

              'Não foi possível excluir a região.';
          }

      });
  }


  /*
   * ==============================
   * FORMATA CEP AO DIGITAR
   * ==============================
   */

  onZipCodeInput(
    rule:
      DeliveryFeeRule,

    field:
      'zipCodeStart' |
      'zipCodeEnd'
  ): void {

    rule[field] =
      this.formatZipCode(
        rule[field]
      );
  }


  /*
   * Retira máscara.
   *
   * 51020-000
   * vira
   * 51020000
   */
  normalizeZipCode(
    value:
      string |
      number |
      null |
      undefined
  ): string {

    return String(
      value ??
      ''
    )
      .replace(
        /\D/g,
        ''
      )
      .slice(
        0,
        8
      );
  }


  /*
   * Formatação enquanto
   * usuário digita.
   */
  formatZipCode(
    value:
      string |
      number |
      null |
      undefined
  ): string {

    const digits =
      this.normalizeZipCode(
        value
      );


    if (
      digits.length <=
      5
    ) {

      return digits;
    }


    return (
      digits.slice(
        0,
        5
      ) +

      '-' +

      digits.slice(
        5
      )
    );
  }


  /*
   * CEP vindo do banco.
   *
   * Como no Prisma estamos usando
   * número para a faixa, um CEP que
   * começa com zero pode chegar
   * sem o primeiro zero.
   *
   * Aqui recolocamos até completar
   * os 8 dígitos para exibição.
   */
  private formatStoredZipCode(
    value:
      string |
      number |
      null |
      undefined
  ): string {

    const digits =
      String(
        value ??
        ''
      )
        .replace(
          /\D/g,
          ''
        );


    if (!digits) {

      return '';
    }


    const padded =
      digits
        .padStart(
          8,
          '0'
        )
        .slice(
          -8
        );


    return (
      padded.slice(
        0,
        5
      ) +

      '-' +

      padded.slice(
        5
      )
    );
  }


  /*
   * ==============================
   * NOME / SLUG
   * ==============================
   */

  onRestaurantNameChange(): void {

    if (
      this.restaurant.slug
    ) {

      return;
    }


    this.restaurant.slug =
      this.generateSlug(
        this.restaurant.name
      );


    this.prepareSlugCheck();
  }


  onSlugChange(): void {

    this.restaurant.slug =
      this.generateSlug(
        this.restaurant.slug
      );


    this.prepareSlugCheck();
  }


  prepareSlugCheck(): void {

    this.slugAvailable =
      null;


    this.slugSuggestions =
      [];


    this.slugError =
      '';


    const slug =
      this.restaurant
        .slug
        .trim();


    if (
      slug.length <
      3
    ) {

      this.slugError =
        'O endereço precisa ter pelo menos 3 caracteres.';


      return;
    }


    this.slugSubject
      .next(
        slug
      );
  }


  configureSlugValidation(): void {

    this.slugSubject
      .pipe(

        debounceTime(
          500
        ),

        distinctUntilChanged(),

        takeUntil(
          this.destroy$
        )

      )
      .subscribe(
        slug => {

          this.checkSlugAvailability(
            slug
          );
        }
      );
  }


  checkSlugAvailability(
    slug:
      string
  ): void {

    this.checkingSlug =
      true;


    this.slugAvailable =
      null;


    this.slugError =
      '';


    const encodedSlug =
      encodeURIComponent(
        slug
      );


    this.api
      .get<any>(

        `/restaurants/address/check` +

        `?value=${encodedSlug}` +

        `&restaurantId=${this.restaurantId ?? ''}`

      )
      .subscribe({

        next:
          response => {

            this.checkingSlug =
              false;


            this.slugAvailable =
              Boolean(
                response?.available
              );


            this.slugSuggestions =
              response?.suggestions ??
              [];


            if (
              !this.slugAvailable
            ) {

              this.slugError =

                response?.message ??

                'Este endereço já está sendo utilizado.';
            }
          },


        error:
          error => {

            this.checkingSlug =
              false;


            this.slugAvailable =
              null;


            this.slugError =

              error
                ?.error
                ?.message ??

              'Não foi possível verificar o endereço.';
          }

      });
  }


  selectSlugSuggestion(
    suggestion:
      string
  ): void {

    this.restaurant.slug =
      suggestion;


    this.prepareSlugCheck();
  }


  /*
   * ==============================
   * SALVA ENDEREÇO
   * ==============================
   */

  saveAddress(): void {

    if (
      !this.restaurantId
    ) {

      return;
    }


    const slug =
      this.generateSlug(
        this.restaurant.slug
      );


    if (
      slug.length <
      3
    ) {

      this.errorMessage =
        'Informe um endereço válido.';


      return;
    }


    if (
      this.slugAvailable !==
      true
    ) {

      this.errorMessage =
        'Verifique se o endereço está disponível antes de salvar.';


      return;
    }


    this.savingAddress =
      true;


    this.clearMessages();


    this.api
      .patch<any>(

        `/restaurants/${this.restaurantId}/address`,

        {
          slug
        }

      )
      .subscribe({

        next:
          response => {

            this.restaurant.slug =

              response?.slug ??
              slug;


            this.savingAddress =
              false;


            this.slugAvailable =
              null;


            this.successMessage =
              'Endereço do cardápio atualizado com sucesso.';
          },


        error:
          error => {

            this.savingAddress =
              false;


            this.errorMessage =

              error
                ?.error
                ?.message ??

              'Não foi possível atualizar o endereço.';
          }

      });
  }


  /*
   * ==============================
   * APARÊNCIA
   * ==============================
   */

  saveAppearance(): void {

    if (
      !this.restaurantId
    ) {

      return;
    }


    this.savingAppearance =
      true;


    this.clearMessages();


    const payload = {

      logoUrl:
        this.restaurant
          .logoUrl
          .trim(),

      primaryColor:
        this.restaurant
          .primaryColor,

      secondaryColor:
        this.restaurant
          .secondaryColor
    };


    this.api
      .patch<any>(

        `/restaurants/${this.restaurantId}/appearance`,

        payload
      )
      .subscribe({

        next:
          response => {

            this.restaurant = {

              ...this.restaurant,

              ...(
                response?.restaurant ??
                response
              )
            };


            this.savingAppearance =
              false;


            this.successMessage =
              'A aparência do cardápio foi atualizada.';
          },


        error:
          error => {

            this.savingAppearance =
              false;


            this.errorMessage =

              error
                ?.error
                ?.message ??

              'Não foi possível salvar a aparência.';
          }

      });
  }


  /*
   * ==============================
   * EQUIPE
   * ==============================
   */

  loadTeam(): void {

    if (
      !this.restaurantId
    ) {

      return;
    }


    this.loadingTeam =
      true;


    this.clearMessages();


    this.api
      .get<TeamResponse>(

        `/restaurants/${this.restaurantId}/team`

      )
      .subscribe({

        next:
          response => {

            this.teamMembers =
              response?.members ??
              [];


            this.teamInvitations =
              response
                ?.invitations ??
              [];


            this.loadingTeam =
              false;
          },


        error:
          error => {

            this.loadingTeam =
              false;


            this.teamMembers =
              [];


            this.teamInvitations =
              [];


            this.errorMessage =

              error
                ?.error
                ?.message ??

              'Não foi possível carregar a equipe.';
          }

      });
  }


  sendInvitation(): void {

    if (
      !this.restaurantId
    ) {

      this.errorMessage =
        'Selecione um restaurante.';


      return;
    }


    const email =
      this.invitationEmail
        .trim()
        .toLowerCase();


    if (
      !email
    ) {

      this.errorMessage =
        'Informe o e-mail do funcionário.';


      return;
    }


    if (
      !this.isValidEmail(
        email
      )
    ) {

      this.errorMessage =
        'Informe um e-mail válido.';


      return;
    }


    this.invitingMember =
      true;


    this.clearMessages();


    this.api
      .post<any>(

        `/restaurants/${this.restaurantId}/invitations`,

        {
          email,

          role:
            this.invitationRole
        }

      )
      .subscribe({

        next:
          response => {

            this.invitingMember =
              false;


            this.invitationEmail =
              '';


            this.invitationRole =
              'ATTENDANT';


            this.successMessage =

              response?.message ??

              'Convite enviado com sucesso.';


            this.loadTeam();
          },


        error:
          error => {

            this.invitingMember =
              false;


            this.errorMessage =

              error
                ?.error
                ?.message ??

              'Não foi possível enviar o convite.';
          }

      });
  }


  updateMemberRole(
    member:
      TeamMember
  ): void {

    if (
      !this.restaurantId ||

      !member?.id ||

      member.role ===
        'OWNER'
    ) {

      return;
    }


    this.updatingMemberId =
      member.id;


    this.clearMessages();


    this.api
      .patch<TeamMember>(

        `/restaurants/${this.restaurantId}/team/${member.id}`,

        {
          role:
            member.role
        }

      )
      .subscribe({

        next:
          updated => {

            this.updatingMemberId =
              null;


            if (
              updated
            ) {

              this.teamMembers =

                this.teamMembers
                  .map(
                    item =>

                      item.id ===
                      member.id

                        ? {
                            ...item,
                            ...updated
                          }

                        : item
                  );
            }


            this.successMessage =
              'Permissão atualizada com sucesso.';
          },


        error:
          error => {

            this.updatingMemberId =
              null;


            this.loadTeam();


            this.errorMessage =

              error
                ?.error
                ?.message ??

              'Não foi possível atualizar a permissão.';
          }

      });
  }


  removeMember(
    member:
      TeamMember
  ): void {

    if (
      !this.restaurantId ||

      !member?.id ||

      member.role ===
        'OWNER'
    ) {

      return;
    }


    const confirmed =
      window.confirm(

        `Remover ${member.name || member.email} da equipe?`

      );


    if (
      !confirmed
    ) {

      return;
    }


    this.removingMemberId =
      member.id;


    this.clearMessages();


    this.api
      .delete<void>(

        `/restaurants/${this.restaurantId}/team/${member.id}`

      )
      .subscribe({

        next:
          () => {

            this.removingMemberId =
              null;


            this.teamMembers =

              this.teamMembers
                .filter(
                  item =>
                    item.id !==
                    member.id
                );


            this.successMessage =
              'Membro removido da equipe.';
          },


        error:
          error => {

            this.removingMemberId =
              null;


            this.errorMessage =

              error
                ?.error
                ?.message ??

              'Não foi possível remover o membro.';
          }

      });
  }


  cancelInvitation(
    invitation:
      TeamInvitation
  ): void {

    if (
      !this.restaurantId ||

      !invitation?.id
    ) {

      return;
    }


    const confirmed =
      window.confirm(

        `Cancelar o convite enviado para ${invitation.email}?`

      );


    if (
      !confirmed
    ) {

      return;
    }


    this.cancellingInvitationId =
      invitation.id;


    this.clearMessages();


    this.api
      .delete<void>(

        `/restaurants/${this.restaurantId}/invitations/${invitation.id}`

      )
      .subscribe({

        next:
          () => {

            this.cancellingInvitationId =
              null;


            this.teamInvitations =

              this.teamInvitations
                .filter(
                  item =>
                    item.id !==
                    invitation.id
                );


            this.successMessage =
              'Convite cancelado.';
          },


        error:
          error => {

            this.cancellingInvitationId =
              null;


            this.errorMessage =

              error
                ?.error
                ?.message ??

              'Não foi possível cancelar o convite.';
          }

      });
  }


  getRoleLabel(
    role:
      TeamMember['role']
  ): string {

    if (
      role ===
      'OWNER'
    ) {

      return 'Proprietário';
    }


    return (

      this.teamRoleOptions
        .find(
          option =>
            option.value ===
            role
        )
        ?.label ??

      role
    );
  }


  getInvitationStatusLabel(
    status:
      TeamInvitation['status']
  ): string {

    const labels:
      Record<
        TeamInvitation['status'],
        string
      > = {

        PENDING:
          'Pendente',

        ACCEPTED:
          'Aceito',

        EXPIRED:
          'Expirado',

        CANCELLED:
          'Cancelado'
      };


    return (
      labels[status] ??
      status
    );
  }


  formatInvitationDate(
    value?:
      string
  ): string {

    if (
      !value
    ) {

      return '-';
    }


    return new Intl
      .DateTimeFormat(
        'pt-BR',
        {
          dateStyle:
            'short',

          timeStyle:
            'short'
        }
      )
      .format(
        new Date(
          value
        )
      );
  }


  /*
   * ==============================
   * LOGO
   * ==============================
   */

  uploadLogo(
    event:
      Event
  ): void {

    if (
      !this.restaurantId
    ) {

      return;
    }


    const input =
      event.target as
        HTMLInputElement;


    const file =
      input.files?.[0];


    if (
      !file
    ) {

      return;
    }


    if (
      !file.type
        .startsWith(
          'image/'
        )
    ) {

      this.errorMessage =
        'Selecione uma imagem válida.';


      input.value =
        '';


      return;
    }


    const formData =
      new FormData();


    formData.append(
      'image',
      file
    );


    this.clearMessages();


    this.api
      .post<any>(

        `/restaurants/${this.restaurantId}/logo`,

        formData
      )
      .subscribe({

        next:
          response => {

            this.restaurant
              .logoUrl =

              response?.logoUrl ??

              response
                ?.restaurant
                ?.logoUrl ??

              '';


            this.successMessage =
              'Logotipo atualizado com sucesso.';


            input.value =
              '';
          },


        error:
          error => {

            this.errorMessage =

              error
                ?.error
                ?.message ??

              'Não foi possível enviar o logotipo.';


            input.value =
              '';
          }

      });
  }


  /*
   * ==============================
   * CAPA
   * ==============================
   */

  uploadCover(
    event:
      Event
  ): void {

    if (
      !this.restaurantId
    ) {

      return;
    }


    const input =
      event.target as
        HTMLInputElement;


    const file =
      input.files?.[0];


    if (
      !file
    ) {

      return;
    }


    if (
      !file.type
        .startsWith(
          'image/'
        )
    ) {

      this.errorMessage =
        'Selecione uma imagem válida.';


      input.value =
        '';


      return;
    }


    const formData =
      new FormData();


    formData.append(
      'image',
      file
    );


    this.clearMessages();


    this.api
      .post<any>(

        `/restaurants/${this.restaurantId}/cover`,

        formData
      )
      .subscribe({

        next:
          response => {

            this.restaurant
              .coverUrl =

              response?.coverUrl ??

              response
                ?.restaurant
                ?.coverUrl ??

              '';


            this.successMessage =
              'Imagem de capa atualizada com sucesso.';


            input.value =
              '';
          },


        error:
          error => {

            this.errorMessage =

              error
                ?.error
                ?.message ??

              'Não foi possível enviar a imagem de capa.';


            input.value =
              '';
          }

      });
  }


  /*
   * ==============================
   * ALTERAR SENHA
   * ==============================
   */

  changePassword(): void {

    this.clearMessages();


    if (
      !this.currentPassword ||

      !this.newPassword ||

      !this.confirmPassword
    ) {

      this.errorMessage =
        'Preencha todos os campos de senha.';


      return;
    }


    if (
      this.newPassword
        .length <
      8
    ) {

      this.errorMessage =
        'A nova senha precisa ter pelo menos 8 caracteres.';


      return;
    }


    if (
      this.newPassword !==
      this.confirmPassword
    ) {

      this.errorMessage =
        'A confirmação não corresponde à nova senha.';


      return;
    }


    if (
      this.currentPassword ===
      this.newPassword
    ) {

      this.errorMessage =
        'A nova senha precisa ser diferente da senha atual.';


      return;
    }


    this.changingPassword =
      true;


    this.api
      .patch<any>(

        '/auth/change-password',

        {
          currentPassword:
            this.currentPassword,

          newPassword:
            this.newPassword
        }

      )
      .subscribe({

        next:
          () => {

            this.changingPassword =
              false;


            this.currentPassword =
              '';


            this.newPassword =
              '';


            this.confirmPassword =
              '';


            this.successMessage =
              'Senha alterada com sucesso.';
          },


        error:
          error => {

            this.changingPassword =
              false;


            this.errorMessage =

              error
                ?.error
                ?.message ??

              'Não foi possível alterar a senha.';
          }

      });
  }


  /*
   * ==============================
   * GETTERS
   * ==============================
   */

  get selectedInvitationRoleDescription():
    string {

    return (

      this.teamRoleOptions
        .find(
          option =>
            option.value ===
            this.invitationRole
        )
        ?.description ??

      ''
    );
  }


  get publicMenuUrl():
    string {

    const slug =

      this.restaurant.slug ||

      'nome-do-restaurante';


    return (
      `https://${slug}.${this.platformDomain}`
    );
  }


  get passwordStrength():
    number {

    const password =
      this.newPassword;


    if (
      !password
    ) {

      return 0;
    }


    let strength =
      0;


    if (
      password.length >=
      8
    ) {

      strength++;
    }


    if (
      /[A-Z]/
        .test(
          password
        )
    ) {

      strength++;
    }


    if (
      /[a-z]/
        .test(
          password
        )
    ) {

      strength++;
    }


    if (
      /\d/
        .test(
          password
        )
    ) {

      strength++;
    }


    if (
      /[^A-Za-z0-9]/
        .test(
          password
        )
    ) {

      strength++;
    }


    return strength;
  }


  get passwordStrengthLabel():
    string {

    const labels = [

      '',

      'Muito fraca',

      'Fraca',

      'Média',

      'Boa',

      'Forte'
    ];


    return (
      labels[
        this.passwordStrength
      ]
    );
  }


  get hasMinimumLength():
    boolean {

    return (
      this.newPassword
        .length >=
      8
    );
  }


  get hasUppercase():
    boolean {

    return (
      /[A-Z]/
        .test(
          this.newPassword
        )
    );
  }


  get hasLowercase():
    boolean {

    return (
      /[a-z]/
        .test(
          this.newPassword
        )
    );
  }


  get hasNumber():
    boolean {

    return (
      /\d/
        .test(
          this.newPassword
        )
    );
  }


  get hasSpecialCharacter():
    boolean {

    return (
      /[^A-Za-z0-9]/
        .test(
          this.newPassword
        )
    );
  }


  /*
   * ==============================
   * HELPERS
   * ==============================
   */

  generateSlug(
    value:
      string
  ): string {

    return String(
      value ??
      ''
    )
      .normalize(
        'NFD'
      )
      .replace(
        /[\u0300-\u036f]/g,
        ''
      )
      .toLowerCase()
      .trim()
      .replace(
        /[^a-z0-9]+/g,
        '-'
      )
      .replace(
        /^-+|-+$/g,
        ''
      )
      .replace(
        /-{2,}/g,
        '-'
      )
      .slice(
        0,
        60
      );
  }


  normalizePhone(
    value:
      string
  ): string {

    return String(
      value ??
      ''
    )
      .replace(
        /\D/g,
        ''
      );
  }


  isValidEmail(
    email:
      string
  ): boolean {

    return (
      /^[^\s@]+@[^\s@]+\.[^\s@]+$/
        .test(
          email
        )
    );
  }


  clearMessages(): void {

    this.successMessage =
      '';


    this.errorMessage =
      '';
  }
}